import { RefObject, useRef } from "react";
import { useUpdateEffect } from "react-use";

const ANIMATION_DURATION = 200;

export interface UseExpandCollapseAnimationParams {
  componentRef: RefObject<HTMLElement>;
  expand?: boolean;
  resetAnimation?: string;
}

export const useExpandCollapseAnimation = ({
  componentRef,
  expand,
  resetAnimation,
}: UseExpandCollapseAnimationParams) => {
  let animationId: number = -1;
  const currentHeight = useRef(0);

  const incrementHeight = (progress: number) => {
    if (componentRef.current) {
      componentRef.current.style.height = `${
        currentHeight.current +
        (componentRef.current.scrollHeight - currentHeight.current) * progress
      }px`;
    }
  };

  const decrementHeight = (progress: number) => {
    if (componentRef.current) {
      componentRef.current.style.height = `${
        componentRef.current.scrollHeight -
        componentRef.current.scrollHeight * progress
      }px`;
    }
  };

  const animateChildren = (show?: boolean) => {
    const start = performance.now();
    const reduceAnimationSetting = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    animationId = requestAnimationFrame(function animate(time) {
      const runtime = time - start;
      const relativeProgress = runtime / ANIMATION_DURATION;
      let progress = Math.min(relativeProgress, 1);

      // if user has defined they want reduced motion, set progress to 1 (100%)
      if (reduceAnimationSetting?.matches) {
        progress = 1;
      }

      if (componentRef.current) {
        componentRef.current.style.overflow = "hidden";
      }

      if (progress < 1) {
        if (show) {
          incrementHeight(progress);
        } else {
          decrementHeight(progress);
        }
        requestAnimationFrame(animate);
      }

      if (progress === 1 && componentRef.current) {
        if (show) {
          componentRef.current.style.height = "auto";
          componentRef.current.style.display = "block";
          componentRef.current.style.overflow = "initial";
          currentHeight.current = componentRef.current.scrollHeight;
        } else {
          componentRef.current.style.height = "";
          componentRef.current.style.display = "none";
          componentRef.current.style.overflow = "";
          currentHeight.current = componentRef.current.scrollHeight;
        }
      }
    });
  };

  // Only animate on updates, not on first load
  useUpdateEffect(() => {
    animateChildren(expand);
  }, [expand, resetAnimation]);

  return animationId;
};
